import React, { useState } from "react";
import { Modal, Button, Header } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Internship } from "./types";
import company from "../../icons/company.svg";
import location from "../../icons/location.svg";
import date from "../../icons/date.svg";
import ApplicationForm from "../applications/components/ApplicationForm";
import FormModal from "../../components/FormModal";
import { IApplicationForm } from "../applications/types";
import AppForm from "../../components/form/Form";
import agent from "../../app/api/agent";
import useToaster from "../../app/hooks/useToaster";
import { useAppForm } from "../../components/form/useForm";
import { prettifyDateWithoutTime } from "../../app/helpers/dateHelpers";

interface IProps {
  internship: Internship;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const InternshipApplicationModal = ({ internship, onCancel: handleCancel, onSuccess }: IProps) => {
  const { t } = useTranslation();
  const { success, apiError } = useToaster();
  const [isSaving, setSaving] = useState(false);

  const formHook = useAppForm<IApplicationForm>({
    defaultValues: { shadowingId: 0, motivationLetter: "", linkedinUrl: "", cv: null, allowPhotos: false },
  });

  const handleSubmit = async (data: IApplicationForm) => {
    setSaving(true);

    try {
      await agent.Applications.create({ ...data, shadowingId: internship.id });
      success(t("applications.notifications.application_sent"));
      onSuccess?.();
      handleCancel?.();
    } catch (error) {
      apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const renderLocation = () => {
    return internship.remote ? t("shadowing.remote") : `${internship.city}, ${internship.address}`;
  };

  const actions = (
    <>
      <Button primary onClick={formHook.handleSubmit(handleSubmit)} disabled={isSaving} loading={isSaving}>
        {t("application.form.actions.send_application")}
      </Button>
    </>
  );

  return (
    <FormModal title="application.modal.header" isOpen={true} onClose={handleCancel} actions={actions} trigger={null}>
      <Modal.Content image>
        <Modal.Description>
          <Header>{internship.title}</Header>
          <p>
            <span>
              <img src={location} alt="location" />
              {renderLocation()}
            </span>
            <span>
              <img src={company} alt="company" />
              {internship.company.name}
            </span>
            <span>
              <img src={date} alt="date" />
              {prettifyDateWithoutTime(internship.startDate)}
              {internship.endDate ? ` – ${prettifyDateWithoutTime(internship.endDate)}` : ""}
            </span>
          </p>

          <AppForm formHook={formHook} onSubmit={handleSubmit} isDisabled={isSaving}>
            <ApplicationForm companyName={internship.company.name} companyCode={internship.company.code} />
          </AppForm>
        </Modal.Description>
      </Modal.Content>
    </FormModal>
  );
};

export default InternshipApplicationModal;
